import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";

// The shared button (Session AQT, Phase 1 design system foundation). Before this, the primary
// green CTA was hand-typed at 6+ structurally different call sites plus QuotesPage's own inline
// actionBtnStyle() helper -- the same file (QuotesPage.tsx) had a live white-on-green contrast
// failure sitting beside a correctly dark-on-green button from the SAME local helper, proving the
// drift is per-call-site, not per-file. This component is the one place the CTA ink rule
// (docs/reference/brand.md: dark #050816 ink on a bright green/blue fill, white on purple) is
// applied, so a caller cannot get it wrong again by retyping a class string.
//
// Destructive is deliberately an outline, not a solid fill: a one-click hard delete already goes
// through ConfirmButton/ConfirmDeleteModal (the platform two-step delete rule); this variant is
// for a lighter-weight "cancel this" / "remove" action, and off-brand `red-600` fills found in the
// wild converge onto the brand --ele-danger token here.

//
// SESSION AUJ (design system Session 2, spec 3.2): two variants, `loading`, ButtonLink and a
// DEFAULT type of "button".
//  - danger: a SOLID red, for the arming second press inside a confirm where a solid red fill
//    exists today. The fill is the fixed #c0392b through the --ele-danger-fill token (white ink
//    5.44:1 in both themes), NEVER bg-ele-danger: that token is #FF5A5A on the dark theme and
//    white on it is 3.06:1 (defect D4).
//  - accent: the solid purple CTA (white ink 7.21:1).
//  - loading: disables, sets aria-busy and shows a 12px spinner before the label. The label text is
//    unchanged, so a test that finds the button by its words still does.
//  - type defaults to "button" (defect D5): a Button inside a <form> used to SUBMIT it, because a
//    <button> with no type is a submit button. A Button that should submit says type="submit".
//    All 18 adopters were read when this landed; none relied on the implicit submit.
//  - className is LAYOUT ONLY (margins, width). Colour and padding belong to variant and size;
//    appContrast.test.ts counts hand-typed CTAs so they cannot drift back.

export const BUTTON_VARIANTS = ["primary", "secondary", "destructive", "ghost", "accent", "danger"] as const;
export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];
export type ButtonSize = "sm" | "md";

const BASE =
  "inline-flex items-center justify-center gap-1.5 rounded-ele font-heading tracking-wide " +
  "transition disabled:opacity-50 disabled:cursor-not-allowed max-sm:min-h-11 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-ele-base";

const SIZE: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-5 py-2.5 text-sm",
};

// Fill + ink pairs read the CTA rule directly: green/blue fills take dark #050816 ink
// (text-ele-bg), never white; a purple fill takes white, per docs/reference/brand.md.
const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-ele-green text-ele-bg shadow-sm hover:brightness-110 focus-visible:ring-ele-green",
  secondary:
    "border border-ele-green text-ele-green bg-transparent hover:bg-ele-green hover:text-ele-bg " +
    "focus-visible:ring-ele-green",
  destructive:
    "border border-ele-danger text-ele-danger bg-transparent hover:bg-ele-danger/10 " +
    "focus-visible:ring-ele-danger",
  ghost: "text-ele-body hover:text-ele-strong bg-transparent focus-visible:ring-ele-purple-accent",
  danger: "bg-ele-danger-fill text-white shadow-sm hover:brightness-110 focus-visible:ring-ele-danger",
  accent: "bg-ele-purple text-white shadow-sm hover:brightness-110 focus-visible:ring-ele-purple",
};

/** The exact class string a variant renders (exported so a test can pin the CTA ink rule). */
export function buttonClasses(variant: ButtonVariant, size: ButtonSize, className = ""): string {
  return `${BASE} ${SIZE[size]} ${VARIANT[variant]} ${className}`.trim();
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
  className?: string;
}

function Spinner() {
  return (
    <span
      aria-hidden
      data-testid="button-spinner"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

export function Button({
  variant = "primary",
  size = "md",
  icon,
  loading = false,
  type = "button",
  className = "",
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClasses(variant, size, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

export interface ButtonLinkProps extends Omit<LinkProps, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  className?: string;
}

/** A react-router <Link> with the identical classes as Button (a navigation that looks like a button). */
export function ButtonLink({ variant = "primary", size = "md", icon, className = "", children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClasses(variant, size, className)} {...rest}>
      {icon}
      {children}
    </Link>
  );
}
